import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Button, Container, Row, Col, Card, Modal } from 'react-bootstrap';
import { Mail, Lock, X, Send, AlertCircle, ArrowRight } from 'react-feather';
import { login, resendConfirmationEmail } from '../../services/api';
import { trackEvent } from '../../components/Tools/analytics';
import logo from "../../assets/Logo-Thinail.png";
import './login.css'

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        trackEvent('Viewed Login Page');
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        
        try {
            // eslint-disable-next-line
            const response = await login({ email, password });
            
            // Đợi để đảm bảo localStorage được cập nhật
            setTimeout(() => {
                // Kiểm tra token đã được lưu chưa
                const accessToken = localStorage.getItem('accessToken');
                const token = localStorage.getItem('token');
                const user = localStorage.getItem('user');
                
                if ((accessToken || token) && user) {
                    // Tạo sự kiện tùy chỉnh để thông báo đăng nhập thành công
                    const authEvent = new Event('auth_state_changed');
                    window.dispatchEvent(authEvent);
                    
                    // Điều hướng đến trang chủ mà không reload
                    navigate('/');
                } else {
                    setError('Đăng nhập thất bại: Không thể lưu thông tin đăng nhập');
                    setIsLoading(false);
                }
            }, 500);
        } catch (error) {
            setIsLoading(false);
            
            if (error.response) {
                const errorMsg = error.response.data.message;
                
                if (errorMsg === 'Vui lòng xác nhận email để đăng nhập.') {
                    setShowModal(true);
                    setModalMessage('Email của bạn chưa được xác nhận. Vui lòng kiểm tra hộp thư hoặc bấm nút dưới đây để gửi lại email xác nhận.');
                } else if (errorMsg === 'Tài khoản không tồn tại. Vui lòng đăng ký.') {
                    setError('Tài khoản không tồn tại. Vui lòng đăng ký.');
                } else if (errorMsg === 'Sai email hoặc mật khẩu.') {
                    setError('Sai email hoặc mật khẩu.');
                } else {
                    setError(`Có lỗi xảy ra: ${errorMsg || 'Không xác định'}`);
                }
            } else {
                setError('Có lỗi kết nối với server. Vui lòng thử lại sau.');
            }
        }
    };

    const handleResendEmail = async () => {
        try {
            await resendConfirmationEmail(email);
            setModalMessage('Email xác nhận đã được gửi lại. Vui lòng kiểm tra hộp thư (hoặc thư mục spam).');
            setResendCooldown(90);

            const interval = setInterval(() => {
                setResendCooldown(prev => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } catch (error) {
            setModalMessage('Lỗi khi gửi lại email xác nhận. Vui lòng thử lại sau.');
        }
    };

    const handleGoogleLogin = () => {
        trackEvent('Clicked Google Login');
        const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
        window.location.href = `${apiBase}/auth/google`;
    };

    return (
        <div className="login-page-wrapper">
            <Container className="login-container">
                <Row className="justify-content-center">
                    <Col md={10} lg={8} xl={6}>
                        <Card className="login-card">
                            <Card.Body className="p-md-5">
                                <div className="text-center mb-4">
                                    <Link to="/" className="login-logo">
                                        <img src={logo} alt="Thinail Logo" className="logo-image" />
                                    </Link>
                                    <h2 className="login-title">Đăng Nhập</h2>
                                    <p className="login-subtitle">Nhập thông tin đăng nhập của bạn để tiếp tục</p>
                                </div>

                                {error && (
                                    <div className="error-message">
                                        <AlertCircle size={16} className="me-2" />
                                        {error}
                                    </div>
                                )}

                                <Form onSubmit={handleSubmit}>
                                    <Form.Group className="form-floating mb-4">
                                        <div className="input-wrapper">
                                            <Mail size={18} className="input-icon" />
                                            <Form.Control
                                                type="email"
                                                id="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="Email của bạn"
                                                required
                                                className="form-input ps-5"
                                                disabled={isLoading}
                                            />
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="form-floating mb-4">
                                        <div className="input-wrapper">
                                            <Lock size={18} className="input-icon" />
                                            <Form.Control
                                                type="password"
                                                id="password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                placeholder="Mật khẩu"
                                                required
                                                className="form-input ps-5"
                                                disabled={isLoading}
                                            />
                                        </div>
                                    </Form.Group>

                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <div className="form-check">
                                            <input 
                                                type="checkbox" 
                                                className="form-check-input" 
                                                id="rememberMe" 
                                            />
                                            <label className="form-check-label" htmlFor="rememberMe">
                                                Ghi nhớ đăng nhập
                                            </label>
                                        </div>
                                        <Link to="/find-password" className="forgot-password">
                                            Quên mật khẩu?
                                        </Link>
                                    </div>

                                    <Button 
                                        type="submit" 
                                        className="login-button w-100 mb-3"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Đang đăng nhập...' : 'Đăng Nhập'}
                                    </Button>

                                    <div className="divider">
                                        <span>Hoặc đăng nhập với</span>
                                    </div>

                                    <Button 
                                        onClick={handleGoogleLogin} 
                                        className="google-login-button w-100 mb-4"
                                        disabled={isLoading}
                                    >
                                        <img
                                            src="https://developers.google.com/identity/images/g-logo.png"
                                            alt="Google logo"
                                            className="google-icon"
                                        />
                                        Tiếp tục với Google
                                    </Button>

                                    <div className="text-center">
                                        <p className="mb-0">
                                            Chưa có tài khoản? 
                                            <Link to="/register" className="register-link ms-1">
                                                Đăng ký ngay <ArrowRight size={14} />
                                            </Link>
                                        </p>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Email Confirmation Modal */}
            <Modal 
                show={showModal} 
                onHide={() => setShowModal(false)} 
                centered 
                className="confirmation-modal"
            >
                <Modal.Header>
                    <Modal.Title>Xác nhận Email</Modal.Title>
                    <Button 
                        variant="link" 
                        className="modal-close-button" 
                        onClick={() => setShowModal(false)}
                    >
                        <X size={20} />
                    </Button>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">
                        <div className="modal-icon">
                            <Mail size={32} />
                        </div>
                        <p className="modal-message">{modalMessage}</p>
                    </div>
                    <div className="d-grid gap-2">
                        <Button
                            className={`resend-button ${resendCooldown > 0 ? 'disabled' : ''}`}
                            onClick={handleResendEmail}
                            disabled={resendCooldown > 0}
                        >
                            {resendCooldown > 0 ? (
                                <span className="countdown">Vui lòng đợi {resendCooldown}s</span>
                            ) : (
                                <>
                                    <Send size={16} className="me-2" />
                                    Gửi lại email xác nhận
                                </>
                            )}
                        </Button>
                        <Button 
                            variant="light" 
                            className="close-modal-button"
                            onClick={() => setShowModal(false)}
                        >
                            Đóng
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default Login;